import { FormModal } from 'components/form/FormModal/FormModal';
import { endOfDay } from 'date-fns';
import { useAuth } from 'hooks';
import { FieldValues } from 'react-hook-form';
import * as yup from 'yup';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DeviceFillupType } from 'types';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { ResolutionForm } from 'views/Planning/modals/ResolveDeliveryModal/ResolveDeliveryModal';
import { useLazyGetDeviceByDeviceIdEsQuery } from 'store/services';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import {
    useLazyCreatePreResolvedTicketQuery,
    useLazyGetDeliveryTicketsByDeviceIdQuery,
} from 'store/services/planning';
import { constructTicket } from 'helpers/planning';

type FillupInputType = DeviceFillupType & {
    name?: string;
    customerName?: string;
    disabled?: boolean;
    deviceId?: string;
    activeTicketId?: string;
};

type FillupInput = {
    deliveries: FillupInputType[];
};

export const defaultFillupValues: FillupInput = {
    deliveries: Array(3).fill({
        amount: 0,
        time: new Date(),
    }),
};

const fillupsSchema = yup
    .object<{ [k in keyof FillupInput]: yup.AnySchema }>({
        deliveries: yup
            .array()
            .of(
                yup.object<{ [k in keyof DeviceFillupType]: yup.AnySchema }>({
                    amount: yup
                        .number()
                        .moreThan(
                            0,
                            'Must select a non-negative gallon amount.'
                        )
                        .required(),
                    time: yup
                        .date()
                        .max(
                            endOfDay(new Date()),
                            'Must select a present or past date.'
                        )
                        .required(),
                })
            )
            .min(1)
            .required(),
    })
    .required();

interface CreateBulkFillupsModalProps extends DefaultOpenModalProps {
    devices?: string[];
    setDevices?: Dispatch<SetStateAction<string[]>>;
    supplierId?: string;
}

export const CreateBulkFillupsModal = ({
    modalOpen,
    setModalOpen,
    devices,
    setDevices,
    supplierId,
}: CreateBulkFillupsModalProps) => {
    const { user } = useAuth();
    const [fillupDefaults, setFillupDefaults] = useState<FillupInput>({
        deliveries: [],
    });

    const dispatch = useDispatch();

    const [queryDevices, { isLoading }] = useLazyGetDeviceByDeviceIdEsQuery();
    const [createTicketQuery] = useLazyCreatePreResolvedTicketQuery();
    const [getActiveTicketQuery, { isLoading: isTicketSearchLoading }] =
        useLazyGetDeliveryTicketsByDeviceIdQuery();

    useEffect(() => {
        if (devices?.length && modalOpen) {
            queryDevices({
                user,
                deviceId: devices.join(' '),
                limit: devices.length,
            }).then((result) => {
                if (result.data?.results) {
                    Promise.all(
                        result.data.results.map(async (device, index) => {
                            const name =
                                device.name ??
                                device.shortDeviceId ??
                                `Device #${index + 1}`;
                            const customerName = device.customer_name;

                            const itemDefaults: FillupInputType = {
                                amount: 0,
                                time: new Date(),
                                deviceId: device.deviceId,
                                name,
                                customerName,
                            };

                            if (supplierId) {
                                await getActiveTicketQuery({
                                    supplierId,
                                    deviceId: device.deviceId,
                                }).then((res) => {
                                    if (
                                        res?.data &&
                                        Object.keys(res.data).length
                                    ) {
                                        itemDefaults.activeTicketId =
                                            Object.keys(res.data)[0];
                                    }
                                });
                            }

                            return itemDefaults;
                        })
                    ).then((defaults) => {
                        setFillupDefaults({ deliveries: defaults });
                    });
                }
            });
        } else {
            setFillupDefaults(defaultFillupValues);
        }
    }, [
        devices,
        queryDevices,
        user,
        getActiveTicketQuery,
        supplierId,
        dispatch,
        modalOpen,
    ]);

    const onSubmit = (data: FieldValues) => {
        const { deliveries } = data;

        if (supplierId && user?.uid) {
            Promise.all(
                deliveries.map((delivery: FillupInputType) => {
                    if (!delivery.time) return Promise.resolve();
                    const newTicket = constructTicket({
                        createdBy: user.uid,
                        supplierId,
                        deviceId: delivery.deviceId,
                    });

                    newTicket.dateDelivered = new Date(delivery.time);
                    newTicket.plannedDate = new Date(delivery.time);
                    newTicket.gallonsDelivered = Number(delivery.amount);
                    newTicket.plannedGallons = Number(delivery.amount);
                    newTicket.status = 'done';

                    return (
                        supplierId &&
                        delivery.deviceId &&
                        createTicketQuery({
                            supplierId,
                            deviceId: delivery.deviceId,
                            ticket: newTicket,
                        })
                    );
                })
            )
                .then(() => {
                    dispatch(
                        activateSnackbar({
                            message: 'Deliveries successfully reported',
                            severity: 'success',
                        })
                    );
                })
                .catch((error) => {
                    dispatch(
                        activateSnackbar({
                            message: error,
                            severity: 'error',
                        })
                    );
                });
        }
    };

    return (
        <FormModal
            defaultValues={fillupDefaults}
            validationSchema={fillupsSchema}
            title="Report Deliveries"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={onSubmit}
            maxWidth="sm"
            testId="CreateBulkFillupsModal"
            isLoading={isLoading || isTicketSearchLoading}
            onClose={() => setDevices && setDevices([])}
        >
            <ResolutionForm
                onRemove={(id) => {
                    setDevices &&
                        setDevices((prevDevices) => {
                            const newDevices = prevDevices.filter(
                                (dev) => dev !== id
                            );

                            if (!newDevices.length) {
                                setModalOpen(false);
                            }

                            return newDevices;
                        });
                }}
            />
        </FormModal>
    );
};
